import { Fragment, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useShowWithDelay } from '~/hooks'

const Spinner = (props) => {
  const { as = Fragment, className, delay = 500, ...rest } = props
  if (!className && !rest.width) {
    rest.width = '2rem'
  }
  const [arc, setArc] = useState(270)
  const ref = useRef()
  const requestRef = useRef()
  const previousTimeRef = useRef()
  const visible = useShowWithDelay(delay)

  useEffect(() => {
    const animate = (time) => {
      if (!ref.current) return

      if (previousTimeRef.current !== undefined) {
        const deltaTime = time - previousTimeRef.current

        // Pass on a function to the setter of the state
        // to make sure we always have the latest state
        setArc((prevCount) => (prevCount + deltaTime * 0.15) % 720)
      }
      previousTimeRef.current = time
      requestRef.current = requestAnimationFrame(animate)
    }

    if (visible) {
      requestRef.current = requestAnimationFrame(animate)
    }
    return () => cancelAnimationFrame(requestRef.current)
  }, [visible])

  useEffect(() => {
    if (!ref.current) return

    const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
      var angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0

      return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY + radius * Math.sin(angleInRadians),
      }
    }

    const describeArc = (x, y, radius, startAngle, endAngle) => {
      var start = polarToCartesian(x, y, radius, endAngle)
      var end = polarToCartesian(x, y, radius, startAngle)

      var largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1'

      var d = [
        'M',
        start.x,
        start.y,
        'A',
        radius,
        radius,
        0,
        largeArcFlag,
        0,
        end.x,
        end.y,
      ].join(' ')

      return d
    }

    ref.current.setAttribute(
      'd',
      describeArc(9, 9, 7, 0, arc > 360 ? arc - 360 : 360 - arc)
    )
  }, [arc])

  const svg = (
    <svg
      viewBox="0 0 18 18"
      className={clsx('animate-spin', className)}
      {...rest}
    >
      <circle
        cx="9"
        cy="9"
        r="7"
        fill="none"
        stroke="currentColor"
        opacity="0.2"
        strokeWidth="2.5"
      />
      <path ref={ref} fill="none" stroke="currentColor" strokeWidth="2.5" />
    </svg>
  )

  const As = as

  return (
    visible &&
    (As === Fragment ? (
      <Fragment>{svg}</Fragment>
    ) : (
      <As className={className}>{svg}</As>
    ))
  )
}

Spinner.propTypes = {
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.symbol]),
  className: PropTypes.string,
  delay: PropTypes.number,
}

export default Spinner
